import * as React from 'react';
import { Subtract } from 'utility-types';

import { ServiceLocator } from '../../app/ServiceLocator';
import { Filter } from '../../ts/helpers/Filter';
import { FlowDetailInfo, FlowListItemInfo } from '../../ts/models/FlowInfo';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { Flows } from '../../ts/resources/Flows';
import { Projects } from '../../ts/resources/Projects';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';

export interface InjectedFlowSpaceDataFetcherProps {
  readonly flowAllowedActions: FlowAllowedActions;
  readonly flowDetailInfo: FlowDetailInfo;
  readonly flowListItemInfo: FlowListItemInfo;
  readonly projectHierarchy: INamedItemInfo[];
}

export interface FlowSpaceDataFetcherProps {
  readonly flowId: string;
}

export function withFlowSpaceDataFetcher<P extends InjectedFlowSpaceDataFetcherProps>(
  Component: React.ComponentType<P>
) {
  return function FlowSpaceDataFetcher(
    props: Subtract<P, InjectedFlowSpaceDataFetcherProps> & FlowSpaceDataFetcherProps
  ) {
    const [ injectedProps, setInjectedProps ] = React.useState<InjectedFlowSpaceDataFetcherProps>(undefined);

    React.useEffect(() => {
      fetchFlowSpaceData(props.flowId, setInjectedProps);
    }, [ props.flowId ]);

    if (!injectedProps) {
      return null;
    }

    return <Component {...props as unknown as P} {...injectedProps}/>;
  };
}

async function fetchFlowSpaceData(
  flowId: string,
  setInjectedProps: (injectedProps: InjectedFlowSpaceDataFetcherProps) => void
) {
  const flows = ServiceLocator.get<Flows>('Flows');
  const flowAllowedActions = await flows.actions(flowId);
  const flowDetailInfo = await flows.getById(flowId);
  const flowListItemInfo = await flows.all()
    .withFilters([Filter.fieldEq('id', flowId)])
    .getFirst();

  const projects = ServiceLocator.get<Projects>('Projects');
  const projectHierarchy = await projects.getProjectHierarchy(flowDetailInfo.project.id);

  setInjectedProps({
    flowAllowedActions,
    flowDetailInfo,
    flowListItemInfo,
    projectHierarchy,
  });
}
